import React from 'react';
import { SlideToggle } from '@ygtechnologies/rubus-ui';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { ProjectFeature, ProjectFeatureConfiguration, ProjectFeatureMode } from '@/types';

interface ProjectFeatureToggleProps {
    toggle: ProjectFeature;
    projectFeatures: ProjectFeatureConfiguration[];
    onChange: (updated: ProjectFeatureConfiguration[]) => void;
}

// The project-level config entry for a feature — i.e. the one with no unit-group / floor override.
const findProjectLevel = (features: ProjectFeatureConfiguration[], feature: ProjectFeature) =>
    features.find((c) => c.feature === feature && c.residentialUnitGroupId === null && !c.floor);

/**
 * Controlled on/off toggle for a single project-level {@link ProjectFeature}, backed by the
 * {@link ProjectFeatureConfiguration} array. Reads the project-level entry for `toggle` and emits a
 * patched copy of the array via `onChange` (add-or-replace; every other entry is left untouched),
 * mirroring the add-or-replace pattern in ProjectSettingsFeatures' handleModeChange.
 *
 * Renders just the control — the caller owns the label/tooltip and surrounding markup. A feature
 * with no explicit project-level entry is treated as enabled, matching the Features tab's default.
 */
export const ProjectFeatureToggle: React.FC<ProjectFeatureToggleProps> = ({ toggle, projectFeatures, onChange }) => {
    const entry = findProjectLevel(projectFeatures, toggle);
    const enabled = entry ? entry.mode === ProjectFeatureMode.Enabled : true;

    const handleChange = (checked: boolean) => {
        const mode = checked ? ProjectFeatureMode.Enabled : ProjectFeatureMode.Disabled;
        onChange(
            entry
                ? arrayReplace(projectFeatures, entry, { ...entry, mode })
                : [...projectFeatures, { feature: toggle, residentialUnitGroupId: null, floor: null, mode }]
        );
    };

    return <SlideToggle checked={enabled} onChange={(e) => handleChange(e.target.checked)} />;
};
